import React from 'react';

import { cn } from '@apps/web/lib/utils';
import {
    type AdminAgentArtifact,
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRendererProps,
    arrayStrings,
    formatCountMetric,
    formatStatusLabel,
    isRecord,
    JsonValue,
    numberValue,
    renderUntrustedString,
    stringValue,
    toRecord,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';
import {
    formatCostMetric,
    formatDurationMetric,
} from '@apps/web/routes/_app/admin/agent/artifacts/token_breakdown';

export type InvestigationLedgerSummary = {
    readonly targetCount: number | null;
    readonly completedCount: number | null;
    readonly skippedSimCount: number | null;
    readonly failedCount: number | null;
    readonly timedOutCount: number | null;
    readonly unresolvedCount: number | null;
    readonly truePositiveCount: number | null;
    readonly falsePositiveCount: number | null;
    readonly uninspectedOrdinals: ReadonlyArray<string>;
    readonly maxWorkerCount: number | null;
    readonly timeoutSeconds: number | null;
    readonly totalCostUsd: string | null;
    readonly caveats: ReadonlyArray<string>;
};

export type InvestigationLedgerEvidence = {
    readonly label: string;
    readonly detail: string;
    readonly source: string | null;
    readonly confidence: string | null;
    readonly citations: ReadonlyArray<string>;
};

export type InvestigationLedgerArtifactRef = {
    readonly title: string;
    readonly artifactType: string | null;
};

export type InvestigationLedgerRow = {
    readonly ordinal: number | null;
    readonly rawTarget: string;
    readonly targetKind: string | null;
    readonly targetId: string | null;
    readonly organizationId: string | null;
    readonly environment: string | null;
    readonly mode: string | null;
    readonly sourceUrl: string | null;
    readonly isSimTrace: boolean;
    readonly status: string;
    readonly verdict: string | null;
    readonly confidence: string | null;
    readonly resolutionSummary: string;
    readonly citations: ReadonlyArray<string>;
    readonly evidence: ReadonlyArray<InvestigationLedgerEvidence>;
    readonly artifactRefs: ReadonlyArray<InvestigationLedgerArtifactRef>;
    readonly caveats: ReadonlyArray<string>;
    readonly unresolvedQuestions: ReadonlyArray<string>;
    readonly nextAction: string | null;
    readonly durationMs: number | null;
    readonly costUsd: string | null;
};

export type InvestigationLedgerPayload = {
    readonly title: string | null;
    readonly instructions: string | null;
    readonly summary: InvestigationLedgerSummary;
    readonly rows: ReadonlyArray<InvestigationLedgerRow>;
};

export function InvestigationLedgerArtifact({
    artifact,
}: AdminArtifactRendererProps) {
    const ledger = getInvestigationLedgerPayload(artifact.payload);
    const metrics = [
        ['Targets', formatCountMetric(ledger.summary.targetCount)],
        ['Completed', formatCountMetric(ledger.summary.completedCount)],
        ['True positives', formatCountMetric(ledger.summary.truePositiveCount)],
        [
            'False positives',
            formatCountMetric(ledger.summary.falsePositiveCount),
        ],
        ['Sim skipped', formatCountMetric(ledger.summary.skippedSimCount)],
        [
            'Uninspected',
            ledger.summary.uninspectedOrdinals.length > 0
                ? ledger.summary.uninspectedOrdinals.join(', ')
                : 'None',
        ],
        ['Workers', formatCountMetric(ledger.summary.maxWorkerCount)],
        [
            'Timeout',
            ledger.summary.timeoutSeconds === null
                ? 'Unknown'
                : `${ledger.summary.timeoutSeconds}s`,
        ],
        ['Cost', formatCostMetric(ledger.summary.totalCostUsd)],
    ] as const;
    return (
        <ArtifactFrame
            artifact={artifact}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            <div className="grid gap-x-4 gap-y-2.5 text-xs sm:grid-cols-3 lg:grid-cols-6">
                {metrics.map(([label, value]) => (
                    <InvestigationLedgerMetric
                        key={label}
                        label={label}
                        value={value}
                    />
                ))}
            </div>
            {ledger.instructions ? (
                <p className="border-border/60 text-muted-foreground border-l pl-2.5 text-xs leading-relaxed">
                    {renderUntrustedString(ledger.instructions)}
                </p>
            ) : null}
            {ledger.summary.caveats.length > 0 ? (
                <InvestigationLedgerList
                    label="Ledger caveats"
                    values={ledger.summary.caveats}
                />
            ) : null}
            {ledger.rows.length > 0 ? (
                <ol className="space-y-2">
                    {ledger.rows.map((row, index) => (
                        <InvestigationLedgerRowView
                            key={`${row.ordinal ?? index}-${row.status}-${index}`}
                            row={row}
                        />
                    ))}
                </ol>
            ) : (
                <p className="text-muted-foreground text-xs">
                    No ledger rows were included in this artifact.
                </p>
            )}
            <details className="group">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                    Raw JSON payload
                </summary>
                <div className="border-border/60 mt-1.5 border-l pl-3">
                    <JsonValue value={artifact.payload} />
                </div>
            </details>
        </ArtifactFrame>
    );
}

function InvestigationLedgerMetric({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string;
}) {
    return (
        <div className="border-border/60 border-l pl-2.5">
            <div className="text-muted-foreground/70 text-[11px]">{label}</div>
            <div className="text-foreground mt-0.5 text-[13px] font-medium break-words tabular-nums">
                {value}
            </div>
        </div>
    );
}

function InvestigationLedgerRowView({
    row,
}: {
    readonly row: InvestigationLedgerRow;
}) {
    const duration = formatDurationMetric(row.durationMs);
    const metadata = (
        [
            ['kind', row.targetKind],
            ['environment', row.environment],
            ['mode', row.mode],
            ['organization', row.organizationId],
            ['target', row.targetId],
            ['source', row.sourceUrl],
            ['duration', duration],
            ['cost', formatCostMetric(row.costUsd)],
        ] as const
    ).flatMap(([label, value]) =>
        value && value !== 'Unknown' ? [[label, value] as const] : [],
    );
    return (
        <li className="border-border/60 border-l pl-3">
            <details>
                <summary className="cursor-pointer py-1">
                    <div className="inline-flex max-w-full flex-wrap items-baseline gap-x-2 gap-y-1 align-top">
                        <span className="text-muted-foreground/70 font-mono text-[11px] tabular-nums">
                            {row.ordinal === null ? '?' : row.ordinal}
                        </span>
                        <span className="text-foreground min-w-0 text-xs font-medium break-words">
                            {renderUntrustedString(row.rawTarget)}
                        </span>
                        <InvestigationLedgerStatusBadge status={row.status} />
                        {row.verdict ? (
                            <span className="text-muted-foreground/70 text-[11px]">
                                {formatStatusLabel(row.verdict)}
                            </span>
                        ) : null}
                        {row.confidence ? (
                            <span className="text-muted-foreground/70 text-[11px]">
                                {renderUntrustedString(row.confidence)}{' '}
                                confidence
                            </span>
                        ) : null}
                        {row.isSimTrace ? (
                            <span className="text-muted-foreground/70 text-[11px]">
                                sim
                            </span>
                        ) : null}
                    </div>
                </summary>
                <div className="space-y-2 pt-1 pb-2">
                    <p className="text-muted-foreground text-xs leading-relaxed">
                        {renderUntrustedString(row.resolutionSummary)}
                    </p>
                    {metadata.length > 0 ? (
                        <dl className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                            {metadata.map(([label, value]) => (
                                <div
                                    key={`${label}-${value}`}
                                    className="flex min-w-0 gap-1"
                                >
                                    <dt className="text-muted-foreground">
                                        {label}
                                    </dt>
                                    <dd className="text-foreground min-w-0 font-mono break-all">
                                        {renderUntrustedString(value)}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    ) : null}
                    <InvestigationLedgerEvidenceList evidence={row.evidence} />
                    <InvestigationLedgerList
                        label="Citations"
                        values={row.citations}
                    />
                    <InvestigationLedgerList
                        label="Caveats"
                        values={row.caveats}
                    />
                    <InvestigationLedgerList
                        label="Unresolved"
                        values={row.unresolvedQuestions}
                    />
                    {row.nextAction ? (
                        <p className="text-muted-foreground text-[11px]">
                            <span className="text-muted-foreground/70">
                                Next
                            </span>{' '}
                            {renderUntrustedString(row.nextAction)}
                        </p>
                    ) : null}
                    {row.artifactRefs.length > 0 ? (
                        <p className="text-muted-foreground text-[11px]">
                            <span className="text-muted-foreground/70">
                                Artifacts
                            </span>{' '}
                            {row.artifactRefs.map((ref, index) => (
                                <React.Fragment
                                    key={`${ref.title}-${ref.artifactType ?? index}`}
                                >
                                    {index > 0 ? (
                                        <span className="text-muted-foreground/40">
                                            {' · '}
                                        </span>
                                    ) : null}
                                    <span>
                                        {renderUntrustedString(ref.title)}
                                    </span>
                                </React.Fragment>
                            ))}
                        </p>
                    ) : null}
                </div>
            </details>
        </li>
    );
}

function InvestigationLedgerStatusBadge({
    status,
}: {
    readonly status: string;
}) {
    const normalized = status.toLowerCase();
    const toneClass =
        normalized === 'completed'
            ? 'bg-emerald-600'
            : normalized === 'failed'
              ? 'bg-red-600'
              : normalized === 'timed_out' || normalized === 'unresolved'
                ? 'bg-amber-500'
                : 'bg-muted-foreground/50';
    return (
        <span className="text-muted-foreground flex items-center gap-1.5 text-[11px]">
            <span className={cn('size-1.5 shrink-0 rounded-full', toneClass)} />
            {formatStatusLabel(status)}
        </span>
    );
}

function InvestigationLedgerEvidenceList({
    evidence,
}: {
    readonly evidence: ReadonlyArray<InvestigationLedgerEvidence>;
}) {
    if (evidence.length === 0) {
        return null;
    }
    return (
        <div className="space-y-1.5">
            <h6 className="text-muted-foreground text-[11px] font-medium">
                Evidence
            </h6>
            <ul className="space-y-1.5">
                {evidence.map((item, index) => (
                    <li
                        key={`${item.label}-${index}`}
                        className="border-border/60 space-y-1 border-l pl-2.5"
                    >
                        <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
                            <span className="text-foreground text-xs font-medium">
                                {renderUntrustedString(item.label)}
                            </span>
                            {item.source ? (
                                <span className="text-muted-foreground/70 text-[11px]">
                                    {renderUntrustedString(item.source)}
                                </span>
                            ) : null}
                            {item.confidence ? (
                                <span className="text-muted-foreground/70 text-[11px]">
                                    {renderUntrustedString(item.confidence)}{' '}
                                    confidence
                                </span>
                            ) : null}
                        </div>
                        <p className="text-muted-foreground text-xs leading-relaxed">
                            {renderUntrustedString(item.detail)}
                        </p>
                        <InvestigationLedgerList
                            label="Citations"
                            values={item.citations}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
}

function InvestigationLedgerList({
    label,
    values,
}: {
    readonly label: string;
    readonly values: ReadonlyArray<string>;
}) {
    if (values.length === 0) {
        return null;
    }
    return (
        <p className="text-muted-foreground text-[11px]">
            <span className="text-muted-foreground/70">{label}</span>{' '}
            {values.map((value, index) => (
                <React.Fragment key={`${label}-${value}-${index}`}>
                    {index > 0 ? (
                        <span className="text-muted-foreground/40">
                            {' · '}
                        </span>
                    ) : null}
                    <span className="font-mono break-all">
                        {renderUntrustedString(value)}
                    </span>
                </React.Fragment>
            ))}
        </p>
    );
}

function getInvestigationLedgerPayload(
    payload: AdminAgentArtifact['payload'],
): InvestigationLedgerPayload {
    const payloadRecord = toRecord(payload);
    const summary = toRecord(payloadRecord['summary']);
    const rows = Array.isArray(payloadRecord['rows'])
        ? payloadRecord['rows']
              .filter(isRecord)
              .flatMap(investigationLedgerRowFromRecord)
        : [];
    return {
        title: stringValue(payloadRecord['title']),
        instructions: stringValue(payloadRecord['instructions']),
        summary: {
            targetCount: numberValue(summary['target_count']),
            completedCount: numberValue(summary['completed_count']),
            skippedSimCount: numberValue(summary['skipped_sim_count']),
            failedCount: numberValue(summary['failed_count']),
            timedOutCount: numberValue(summary['timed_out_count']),
            unresolvedCount: numberValue(summary['unresolved_count']),
            truePositiveCount: numberValue(summary['true_positive_count']),
            falsePositiveCount: numberValue(summary['false_positive_count']),
            uninspectedOrdinals: arrayStrings(summary['uninspected_ordinals']),
            maxWorkerCount: numberValue(summary['max_worker_count']),
            timeoutSeconds: numberValue(summary['timeout_seconds']),
            totalCostUsd: stringValue(summary['total_cost_usd']),
            caveats: arrayStrings(summary['caveats']),
        },
        rows,
    };
}

function investigationLedgerRowFromRecord(
    record: Record<string, unknown>,
): ReadonlyArray<InvestigationLedgerRow> {
    const rawTarget = stringValue(record['raw_target']);
    const status = stringValue(record['status']);
    const resolutionSummary = stringValue(record['resolution_summary']);
    if (!rawTarget || !status || !resolutionSummary) {
        return [];
    }
    return [
        {
            ordinal: numberValue(record['ordinal']),
            rawTarget,
            targetKind: stringValue(record['target_kind']),
            targetId: stringValue(record['target_id']),
            organizationId: stringValue(record['organization_id']),
            environment: stringValue(record['environment']),
            mode: stringValue(record['mode']),
            sourceUrl: stringValue(record['source_url']),
            isSimTrace: record['is_sim_trace'] === true,
            status,
            verdict: stringValue(record['verdict']),
            confidence: stringValue(record['confidence']),
            resolutionSummary,
            citations: arrayStrings(record['citations']),
            evidence: investigationLedgerEvidenceFromUnknown(
                record['evidence'],
            ),
            artifactRefs: investigationLedgerArtifactRefsFromUnknown(
                record['artifact_refs'],
            ),
            caveats: arrayStrings(record['caveats']),
            unresolvedQuestions: arrayStrings(record['unresolved_questions']),
            nextAction: stringValue(record['next_action']),
            durationMs: numberValue(record['duration_ms']),
            costUsd: stringValue(record['cost_usd']),
        },
    ];
}

function investigationLedgerEvidenceFromUnknown(
    value: unknown,
): ReadonlyArray<InvestigationLedgerEvidence> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((item) => {
        const label = stringValue(item['label']);
        const detail = stringValue(item['detail']);
        if (!label || !detail) {
            return [];
        }
        return [
            {
                label,
                detail,
                source: stringValue(item['source']),
                confidence: stringValue(item['confidence']),
                citations: arrayStrings(item['citations']),
            },
        ];
    });
}

function investigationLedgerArtifactRefsFromUnknown(
    value: unknown,
): ReadonlyArray<InvestigationLedgerArtifactRef> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((item) => {
        const title = stringValue(item['title']);
        if (!title) {
            return [];
        }
        return [
            {
                title,
                artifactType: stringValue(item['artifact_type']),
            },
        ];
    });
}
